import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import LoadingButton from "@mui/lab/LoadingButton";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Button, TextareaAutosize } from "@mui/material";

import Title from "../../../common/Title";
import InputFieldAutoComplete from "../../../common/InputFieldAutoComplete";

import useAlert from "../../../../hooks/useAlert";
import useAxios from "../../../../hooks/useAxios";
import { __portinTmobilej } from "../../../../api/sim";
import { __fetchPlans } from "../../../../api/plan";

const TmobilejPortinForm = () => {
  const [rawData, setRawData] = useState("");
  const [linesData, setLinesData] = useState([]);
  const [formData, setFormData] = useState(null);
  const [plans, setPlans] = useState([]);

  const { setAlertMessage } = useAlert();
  const initialData = { lines: [], plan_id: null };
  const [response, call, loading] = useAxios({
    ...__portinTmobilej,
  });
  const [planResponse, planCall, planLoading] = useAxios({
    ...__fetchPlans,
  });
  const nav = useNavigate();

  const lineValidationSchema = yup.object({
    msisdn: yup
      .string("msisdn")
      .min(10, "Should be exactly 10 digits long")
      .max(10, "Should be exactly 10 digits long")
      .required(),
    current_carrier_name: yup.string("current_carrier_name").required(),
    current_account_number: yup.string("current_account_number").required(),
    current_account_password: yup.string("current_account_password").required(),
    street: yup.string("street").required(),
    street2: yup.string("street2"),
    city: yup.string("city").required(),
    state: yup.string("state").required(),
    zipcode: yup
      .string("zipcode")
      .test("Digits only", "The field should have 5 digits only", (value) =>
        value && value.length ? /^\d{5}$/.test(value) : true
      )
      .required(),
    name: yup.string("name").optional(),
    iccid: yup
      .string("iccid")
      .test("ICCID format", "ICCID must be 19 or 20 digits", (value) =>
        value && value.length ? /^\d{19,20}$/.test(value) : true
      )
      .optional(),
  });

  useEffect(() => {
    planCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (planResponse && planResponse.status) {
      setPlans(planResponse.data.data || planResponse.data);
    }
  }, [planResponse]);

  useEffect(() => {
    if (formData) {
      // Find the selected plan to get plan_name
      const plan = plans.find(p => p.id === formData.plan_id);
      
      call({
        data: {
          plan_id: formData.plan_id,
          plan_name: plan?.plan_name || plan?.name || null,
          lines: [...linesData],
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  useEffect(() => {
    if (!response) return;
    if (response.status) {
      setAlertMessage({ type: "success", message: "Portin queue started" });
      setLinesData([]);
      nav("/tmobilej/portin");
    }
    formik.setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  const validateFunction = (data) => {
    const errors = {};
    const formatedLines = [];

    if (!data) {
      return errors;
    }

    const msisdns = [];

    const lines = data.split("\n");
    lines.some((line, idx) => {
      const data = line.split(",");
      if (data.length < 8) {
        errors.lines = `Invalid format in line ${
          idx + 1
        }: ${line}. At least 8 fields required (MSISDN through State)`;
        return true;
      }

      try {
        const line_data = line.split(",");
        const obtainedData = {
          msisdn: line_data[0].trim(),
          current_carrier_name: line_data[1].trim(),
          current_account_number: line_data[2].trim(),
          current_account_password: line_data[3].trim(),
          street: line_data[4].trim(),
          street2: line_data[5] ? line_data[5].trim() : "",
          city: line_data[6].trim(),
          state: line_data[7].trim(),
          zipcode: line_data[8].trim(),
          name: line_data[9] ? line_data[9].trim() : "",
          iccid: line_data[10] ? line_data[10].trim() : "",
        };

        // Remove empty iccid if not provided
        if (!obtainedData.iccid) {
          delete obtainedData.iccid;
        }

        lineValidationSchema.validateSync(obtainedData);

        if (isNaN(obtainedData.msisdn)) {
          throw new Error("Invalid Number");
        }
        formatedLines.push(obtainedData);

        msisdns.push(obtainedData.msisdn);

        if (msisdns.indexOf(obtainedData.msisdn) !== idx) {
          errors.lines = `Duplicate msisdn value in line ${idx + 1} `;
          return true;
        }
      } catch (err) {
        console.log(err);
        errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
        return true;
      }

      return false;
    });

    if (!errors.lines) {
      setLinesData(formatedLines);
    }

    return errors;
  };

  const validationSchema = yup.object({
    plan_id: yup
      .number("plan_id")
      .required("Plan is required")
      .nullable(),
  });

  const formik = useFormik({
    initialValues: { ...initialData },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const requestData = {
        plan_id: values.plan_id,
        lines: values.lines || [],
      };

      setFormData(requestData);
    },
    validateOnChange: true,
  });

  const handleParse = (inputFile) => {
    const reader = new FileReader();

    reader.onload = async ({ target }) => {
      setRawData(target.result);
      formik.setErrors(validateFunction(target.result));
    };
    reader.readAsText(inputFile);
  };
  const handleFileChange = (e) => {
    const allowedExtensions = ["csv"];

    // Check if user has entered the file
    if (e.target.files.length) {
      const inputFile = e.target.files[0];

      const fileExtension = inputFile?.type.split("/")[1];
      if (!allowedExtensions.includes(fileExtension)) {
        formik.setErrors({
          ...formik.errors.lines,
          upload: "Please input a csv file",
        });
        return;
      }

      handleParse(inputFile);
    }
  };

  if (loading || planLoading) {
    return <Title content="Please wait..." variant="subtitle2" gutterBottom />;
  }

  return (
    <>
      <Box
        sx={{ width: "100%" }}
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Title variant="h5" content="Portin Sims" sx={{ mb: 3 }} gutterBottom />
        <Grid
          sx={{ flexGrow: 1 }}
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={12} minWidth={350}>
            <FormControl
              error={
                formik.touched.plan_id && Boolean(formik.errors.plan_id)
                  ? true
                  : false
              }
              sx={{ width: "100%" }}
            >
              <InputFieldAutoComplete
                isSelect
                options={[
                  ...plans.reduce((acc, plan) => {
                    const val = {
                      value: plan.id,
                      label: plan.plan_name || plan.name,
                    };
                    acc.push(val);
                    return acc;
                  }, []),
                ]}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                name="plan_id"
                label="Plan"
                id="plan_id"
                onChange={(e, value) => {
                  formik.setFieldValue("plan_id", value?.value || null);
                }}
              />
              {formik.touched.plan_id && Boolean(formik.errors.plan_id) ? (
                <FormHelperText id="plan_id-error-text">
                  {formik.errors.plan_id}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl
              error={Boolean(formik.errors.upload) ? true : false}
              sx={{ width: "100%" }}
            >
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                sx={{ marginRight: "1rem" }}
              >
                Upload CSV
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {Boolean(formik.errors.upload) ? (
                <FormHelperText id="upload-error-text">
                  {formik.errors.upload}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl
              error={Boolean(formik.errors.lines) ? true : false}
              sx={{ width: "100%" }}
            >
              <FormHelperText id="lines-helper-text">
                Add in the format "MSISDN, Current Carrier Name, Account Number,
                Account Password, Street, Street2, City, State, Zipcode, Name (optional), ICCID (optional)"
                <br />
                <em>Note: ICCID must be 19-20 digits if provided. All fields except Name and ICCID are required.</em>
              </FormHelperText>
              <TextareaAutosize
                multiline
                rows={6}
                aria-label="minimum height"
                placeholder={`Add in the format\n"MSISDN, Current Carrier Name, Account Number, Account Password, Street, Street2, City, State, Zipcode, Name (optional), ICCID (optional)"\nExample:\n1234567890,Carrier,Account123,Password123,123 Main St,Apt 1,City,ST,12345,John Doe,89012345678901234567`}
                style={{ minWidth: 550 }}
                name="lines"
                minRows={5}
                id="lines"
                value={rawData}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const data = e.target.value;
                  setRawData(data);
                  formik.setErrors(validateFunction(data));
                }}
              />
              {Boolean(formik.errors.lines) ? (
                <FormHelperText id="lines-error-text">
                  {formik.errors.lines}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid
            container
            sx={{ mt: 3 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <LoadingButton
                style={{ marginRight: "5px" }}
                disabled={
                  formik.errors.lines ||
                  !linesData.length ||
                  !formik.values.plan_id ||
                  formik.errors.plan_id
                }
                onClick={() =>
                  !loading && !formik.isSubmitting && !planLoading && formik.submitForm()
                }
                size="large"
                loading={loading}
                variant="contained"
              >
                {"Portin SIMs"}
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default TmobilejPortinForm;
